import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_LABELS = {
  founding_monthly: 'Founding Monthly · $99/mo · Lifetime founding rate',
  founding_annual: 'Founding Annual · $899 prepay year 1, then $99/mo lifetime',
  standard_monthly: 'Standard · $249/mo',
};

function PartnersPlatformsApply() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    name: '',
    website_url: '',
    applicant_name: '',
    applicant_email: '',
    applicant_linkedin: '',
    pitch: '',
    current_mrr: '',
    plan: params.get('plan') || 'founding_monthly',
  });

  useEffect(() => {
    const planParam = params.get('plan');
    if (planParam && PLAN_LABELS[planParam]) {
      setForm((f) => ({ ...f, plan: planParam }));
    }
  }, [params]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.pitch.trim().length < 20) {
      toast.error('Pitch must be at least 20 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/platform-partners/apply`, form);
      setSuccess(res.data);
      toast.success('Application received');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="platforms-apply-success">
        <SEOHead title="Application received — theailedger.ai" />
        <div className="max-w-xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 bg-[#00FF94]/15 border border-[#00FF94]/30 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Check className="h-7 w-7 text-[#00FF94]" />
          </div>
          <h1 className="text-3xl font-bold mb-3">We've got it.</h1>
          <p className="text-white/70 leading-relaxed mb-6">
            {success.message}<br/><br/>
            Once approved, you'll get a Stripe Checkout link, your dedicated
            landing page, and a self-serve analytics dashboard URL.
          </p>
          <button onClick={() => navigate('/')} data-testid="apply-success-home-btn"
            className="text-[#00F0FF] hover:underline">← Back to theailedger.ai</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="platforms-apply-page">
      <SEOHead title="Apply — Featured Platform Partner — theailedger.ai" />
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/partners/platforms" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to platforms
          </Link>
          <span className="text-xs text-white/40">Step 1 of 1 · Application</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-2">Apply for Featured Platform Partner</h1>
        <p className="text-white/65 text-sm leading-relaxed mb-8">
          We review every application within 48 hours. Approved applicants receive a
          Stripe Checkout link, dedicated landing page, and analytics dashboard URL.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="platforms-apply-form">
          <Field label="Plan" required>
            <select value={form.plan} onChange={update('plan')} required
              data-testid="apply-plan-select"
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#00F0FF]">
              {Object.entries(PLAN_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>

          <Field label="Company / platform name" required>
            <Input value={form.name} onChange={update('name')} required testid="apply-name-input"
              placeholder="e.g. Lovable" />
          </Field>

          <Field label="Website URL" required>
            <Input value={form.website_url} onChange={update('website_url')} required
              type="url" testid="apply-website-input" placeholder="https://lovable.dev" />
          </Field>

          <Field label="Your name" required>
            <Input value={form.applicant_name} onChange={update('applicant_name')} required
              testid="apply-applicant-name" placeholder="Founder / partnerships lead" />
          </Field>

          <Field label="Work email" required>
            <Input value={form.applicant_email} onChange={update('applicant_email')} required
              type="email" testid="apply-applicant-email" />
          </Field>

          <Field label="Your LinkedIn (optional but recommended)">
            <Input value={form.applicant_linkedin} onChange={update('applicant_linkedin')}
              testid="apply-applicant-linkedin" placeholder="https://linkedin.com/in/…" />
          </Field>

          <Field label="Brief pitch (what does your platform do?)" required>
            <textarea value={form.pitch} onChange={update('pitch')} required rows={5}
              data-testid="apply-pitch-textarea"
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#00F0FF] font-mono text-sm leading-relaxed"
              placeholder="One paragraph: who you serve, what you replace, and why a creator on theailedger.ai should care."
              minLength={20} maxLength={2000}/>
          </Field>

          <Field label="Current MRR (or stage)">
            <Input value={form.current_mrr} onChange={update('current_mrr')}
              testid="apply-mrr-input" placeholder="e.g. Pre-revenue · $5k MRR · YC W26" />
          </Field>

          <button type="submit" disabled={submitting} data-testid="apply-submit-btn"
            className="w-full bg-[#00F0FF] text-black font-bold py-3 rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin"/>Submitting…</> : 'Submit application'}
          </button>
          <p className="text-xs text-white/40 text-center">
            No payment now. We'll email you a Stripe Checkout link after we approve.
          </p>
        </form>
      </main>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider font-bold text-white/60 mb-1.5">
        {label}{required && <span className="text-[#00F0FF] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function Input({ value, onChange, required, type = 'text', testid, placeholder }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      placeholder={placeholder}
      className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#00F0FF]"
    />
  );
}

export default PartnersPlatformsApply;
